import z from 'zod';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { createAsyncZodFormResolver, getDefaults, useFormControls } from '@mews/framework';
import { EnterpriseProfileOptions, LearningPath, useRepositoryMutation } from '@mews-commander/data-access';
import { EnterpriseProfilePrivilegesEnumNames } from '@mews-commander/legacy-data-structures';
import { isPrivilegeValue } from '@mews-commander/legacy-entities/src/utils';
import { useAsyncEmailValidation } from '@mews-commander/form';
import { RolesAndPermissionsOption } from '../../types';
import { FieldNames } from '../FieldNames';

const commonSchema = z.object({
    [FieldNames.Emails]: z.array(z.string().email()).min(1).default([]),
    [FieldNames.EnterpriseIds]: z.array(z.string()).min(1).default([]),
    [FieldNames.AssignedLearningPath]: z.nativeEnum(LearningPath).refine((value) => value),
    [FieldNames.ReceiveCustomerMessages]: z.boolean().optional().default(false),
    [FieldNames.Reset2FA]: z.boolean().optional().default(false),
    [FieldNames.RolesAndPermissions]: z.nativeEnum(RolesAndPermissionsOption).default(RolesAndPermissionsOption.Roles),
    [FieldNames.RoleIds]: z.array(z.string()).optional().default([]),
    [FieldNames.Privileges]: z
        .array(z.custom<EnterpriseProfilePrivilegesEnumNames>(isPrivilegeValue))
        .optional()
        .default([]),
});

const schema = z.discriminatedUnion(FieldNames.RolesAndPermissions, [
    commonSchema.extend({
        [FieldNames.RolesAndPermissions]: z.literal(RolesAndPermissionsOption.Roles),
        [FieldNames.RoleIds]: z.array(z.string()).min(1),
    }),
    commonSchema.extend({
        [FieldNames.RolesAndPermissions]: z.literal(RolesAndPermissionsOption.CustomizedPermissions),
        [FieldNames.Privileges]: z.array(z.custom<EnterpriseProfilePrivilegesEnumNames>(isPrivilegeValue)).min(1),
    }),
    commonSchema.extend({
        [FieldNames.RolesAndPermissions]: z.literal(RolesAndPermissionsOption.Admin),
    }),
    commonSchema.extend({
        [FieldNames.RolesAndPermissions]: z.literal(RolesAndPermissionsOption.NoPermissions),
    }),
]);

export type AddUsersFormValues = z.infer<typeof schema>;

const defaultValues = getDefaults(commonSchema);

export const useAddUsersForm = ({ setEmailsSent }: { setEmailsSent: (emails: string[]) => void }) => {
    const addUsersMutation = useRepositoryMutation('userProfiles', 'add', {
        invalidateQueries: { userProfiles: ['getAll'] },
        onSuccess: (data) => setEmailsSent(data.emails || []),
    });

    const { mutateAsync } = useRepositoryMutation('userProfiles', 'validateEmails');
    const validateEmails = useAsyncEmailValidation(mutateAsync);

    const resolver = createAsyncZodFormResolver(
        schema.and(
            z.object({
                [FieldNames.Emails]: z.array(z.string().email()).min(1).superRefine(validateEmails),
            })
        )
    );

    const otherMethods = useFormControls({
        onSubmit: (formData) => addUsersMutation.mutate(parseFormDataToPayload(formData)),
        resolver,
        defaultValues,
    });

    return {
        ...otherMethods,
        isPending: addUsersMutation.isPending,
    };
};

const parseFormDataToPayload = (formData: AddUsersFormValues) => {
    const { assignedLearningPath, enterpriseIds, receiveCustomerMessages, reset2FA } = formData;

    const emails = formData[FieldNames.Emails];

    const permissionsSelected = formData[FieldNames.RolesAndPermissions];
    const isAdmin = permissionsSelected === RolesAndPermissionsOption.Admin;

    const options: EnterpriseProfileOptions = {
        receiveCustomerMessages,
        resetUsersTwoFactorAuthentication: reset2FA,
    };

    const privileges = formData[FieldNames.Privileges]?.length
        ? pipe(
              formData[FieldNames.Privileges],
              array.map((privilege) => [privilege, true]),
              Object.fromEntries
          )
        : undefined;

    const roleIdsSelected = formData[FieldNames.RoleIds];
    const roleIds = roleIdsSelected?.length ? roleIdsSelected : null;

    return { assignedLearningPath, enterpriseIds, emails, isAdmin, options, privileges, roleIds };
};
